import React, { useMemo } from 'react';
import { Trees } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import GlobalNatureRiskGate from 'src/app/portfolio/views/global-nature-risk/GlobalNatureRiskGate';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useGetSiteFilterOptionsQuery, useGetPortfolioStateOfNatureQuery } from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import SonFactorsPanel from 'src/shared/components/son-factors-panel/SonFactorsPanel';
import useGnrFilters from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrFilters';
import { siteEstateFiltersOf } from 'src/app/portfolio/views/global-nature-risk/siteEstateFilters';
import useGnrAvailableTimePeriods from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrAvailableTimePeriods';
import TimePeriodFilter from 'src/shared/components/filters/time-period-filter/TimePeriodFilter';
import { sanitizeSelectedFilters } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import GnrActiveFilters from '../site-list/components/filters/GnrActiveFilters';
import SiteListFilters from '../site-list/components/filters/SiteListFilters';

const GnrStateOfNatureContent = () => {
    const { currentCustomerId } = useCustomer();
    const {
        selectedFilters,
        applyFilters,
        resetFilters,
        resetFiltersAndPeriod,
        updateFilters,
        selectedAt,
        setSelectedAt,
        search,
        setSearch,
    } = useGnrFilters();
    const { timePeriods } = useGnrAvailableTimePeriods(currentCustomerId);

    const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetSiteFilterOptionsQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );

    const siteFilterOptions = filterOptionsData?.organisation?.siteFilterOptions;

    const validatedFilters = useMemo(
        () => sanitizeSelectedFilters(selectedFilters, siteFilterOptions),
        [selectedFilters, siteFilterOptions]
    );

    // Every query on this page reads the estate through one set of filters, the
    // time period among them.
    const filterArgs = useMemo(
        () => siteEstateFiltersOf({ selectedFilters: validatedFilters, at: selectedAt }),
        [validatedFilters, selectedAt]
    );

    const { data: stateOfNatureData, isLoading: isLoadingStats } = useGetPortfolioStateOfNatureQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const stats = stateOfNatureData?.organisation?.portfolioRiskStats;
    const categories = stats?.sonCategoryBreakdown ?? [];

    return (
        <StandardAppLayout
            title='State of Nature'
            subTitle='Current biodiversity conditions against global index'
            classNames={{ otherActions: '!justify-start !p-0' }}
            stickyHeader={false}
            otherActions={
                <div className='flex items-center gap-3'>
                    <TimePeriodFilter periods={timePeriods} selectedValue={selectedAt} onSelect={setSelectedAt} />
                    <SiteListFilters
                        selectedFilters={selectedFilters}
                        onApply={applyFilters}
                        onClear={resetFilters}
                        filterOptions={siteFilterOptions}
                        isLoadingFilterOptions={isLoadingFilterOptions}
                        businessCentralId={currentCustomerId ?? undefined}
                    />
                </div>
            }
            mainContent={
                <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='gnr-state-of-nature-main'>
                    <GnrActiveFilters
                        selectedFilters={validatedFilters}
                        countries={siteFilterOptions?.countries}
                        isicCodes={siteFilterOptions?.isicCodes}
                        onRemove={updateFilters}
                        onClearAll={resetFiltersAndPeriod}
                        matchingSiteCount={stats?.assessedSiteCount}
                        selectedPeriodLabel={timePeriods.find(p => p.value === selectedAt)?.label ?? null}
                        onClearPeriod={() => setSelectedAt(null)}
                        searchTerm={search}
                        onClearSearch={() => setSearch('')}
                    />

                    <div className='flex flex-row gap-4'>
                        <div className='flex-[2] flex flex-col'>
                            <RiskScoreCard
                                title='Overall State of Nature'
                                value={stats?.meanStateOfNature ?? null}
                                scoreType='Condition'
                                isLoading={isLoadingStats}
                                size='lg'
                                slug='state-of-nature'
                                icon={Trees}
                                className='h-full'
                            />
                        </div>
                        {categories.map(category => (
                            <div key={category.sourceKey} className='flex-1 flex flex-col'>
                                <RiskScoreCard
                                    title={category.name}
                                    value={category.score}
                                    scoreType='Condition'
                                    fullBadgeLabel={false}
                                    isLoading={isLoadingStats}
                                    size='md'
                                    slug={`son_${category.sourceKey}`}
                                    showBarometer={false}
                                    className='h-full'
                                />
                            </div>
                        ))}
                    </div>

                    {categories.some(c => (c.factors?.length ?? 0) > 0) && (
                        <div className='flex flex-col gap-3'>
                            <div className='flex items-center justify-between'>
                                <span className='text-xl text-black'>State of Nature category breakdown</span>
                                <ReportingFrameworks slugPrefix='gnr-son' />
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                {categories.map(category => (
                                    <SonFactorsPanel
                                        key={`${category.sourceKey}-factors`}
                                        title={category.name}
                                        slug={category.sourceKey}
                                        factors={(category.factors ?? [])
                                            .map(f => ({
                                                name: f.name,
                                                sourceKey: f.sourceKey,
                                                weight: f.weight,
                                                score: f.score ?? null,
                                            }))
                                            .sort((a, b) => {
                                                if (a.score === null && b.score === null) return 0;
                                                if (a.score === null) return 1;
                                                if (b.score === null) return -1;
                                                return b.score - a.score;
                                            })}
                                        isLoading={isLoadingStats}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

/** GnrStateOfNature, shown only to a user who may see Global Nature Risk. */
const GnrStateOfNature = () => (
    <GlobalNatureRiskGate>
        <GnrStateOfNatureContent />
    </GlobalNatureRiskGate>
);

export default GnrStateOfNature;
